/**
 * MOCK · ASSUMED (CT-5713) — the Next Best Action suggestions of a record.
 *
 * Shapes follow the contract assumed in CT-5713 and are marked
 * `TODO(CT-5713 / backend)` at the call site. The seed is the prototype's
 * (record PER-0104): a draft, the Renewal playbook workflow, customer
 * references and bringing in the signer — the same for every customer, worded
 * with the record's name. Starting the workflow drops it from the list and puts
 * it on the record's header (`active_workflow`), as the prototype does.
 *
 * QA switch, read from the URL on every request (`?mockNba=…`), like CT-5612's
 * `?mockFail=`:
 *
 *   noNba        no suggestions (How to approach still answers, CT-5714)
 *   empty        no suggestions and no recap
 *   fail         the request fails
 *   restricted   the viewer sees only what they may see
 *   reviewer     someone else is reviewing the first draft
 *   newAction    a new suggestion has arrived
 *   start_fail   Start workflow fails
 */

import { getMockEntityHeaders } from './entityHeader.mock.js';

export const NBA_SCENARIOS = Object.freeze(['noNba', 'empty', 'fail', 'restricted', 'reviewer', 'newAction', 'start_fail']);

const MINUTE_MS = 60 * 1000;

let scenarioOverride = null;
/** Workflows started per record, and the suggestions they took off the list. */
let started = new Map();

const recordKey = (entityType, entityId) => `${entityType}/${entityId}`;

const readScenario = () => {
  if (scenarioOverride) return scenarioOverride;
  try {
    const value = new URLSearchParams(window.location.search).get('mockNba');
    return NBA_SCENARIOS.includes(value) ? value : null;
  } catch {
    return null;
  }
};

export const getMockNbaScenario = readScenario;

const nameOf = (entityType, entityId) => {
  const rows = getMockEntityHeaders()[entityType] ?? [];
  const row = rows.find((candidate) => (candidate.entity_id ?? candidate.entityId ?? candidate.id) === entityId);
  return row?.title ?? row?.name ?? 'this contact';
};

const handToConcierge = (id, description) => ({
  id,
  title: 'Hand it to Concierge',
  description,
  recommended: false,
  action: { type: 'hand_to_concierge' },
});

const buildItems = (name) => [
  {
    id: 'nba-timeline',
    title: 'Send the migration timeline with the data-residency note',
    context: 'Renewal · closes in 20 days.',
    description: `${name} asked for it in writing on Aug 12 and Aug 18.`,
    tags: [
      { key: 'draft_ready', label: 'Draft ready' },
      { key: 'email', label: 'Email' },
    ],
    primary_action: { type: 'review_draft', draft_id: 'd-timeline' },
    details: {
      source: { label: `${name}'s emails, Aug 12 and Aug 18` },
      ways: [
        {
          id: 'w-timeline-email',
          title: 'Email the timeline with the note attached',
          description: 'The draft already answers both requests.',
          recommended: true,
          action: { type: 'review_draft', draft_id: 'd-timeline' },
        },
        handToConcierge('w-timeline-concierge', 'Concierge sends it and checks with you before the message goes out.'),
      ],
      steps: [],
    },
  },
  {
    id: 'nba-playbook',
    title: `Add ${name} to the Renewal playbook`,
    context: 'Renewal · closes in 20 days · needs your confirmation.',
    description: 'Two escalations are still open.',
    summary: '5 phases · starts with Kick-off email',
    tags: [
      { key: 'workflow', label: 'Workflow' },
      { key: 'steps', label: '5 steps' },
      { key: 'duration', label: 'About 3 weeks' },
    ],
    primary_action: { type: 'review_workflow', workflow_id: 'wf-renewal' },
    details: { source: { label: 'Renewal on Nov 3' }, ways: [], steps: [] },
    workflow: {
      name: 'Renewal playbook',
      estimated_time: 'About 3 weeks',
      steps: [
        {
          id: 's1',
          title: 'Kick-off email',
          starts_now: true,
          description: `Concierge drafts the renewal kick-off email to ${name}. Agent · Today · needs your approval.`,
        },
        {
          id: 's2',
          title: 'Pricing call',
          description: `Book the pricing call with ${name} and Daniel Reyes, who signs above $250K. Priya Nair · Week 1.`,
        },
        { id: 's3', title: 'Legal review', description: 'Legal reviews the governance addendum before pricing is final. Legal · Week 2.' },
        { id: 's4', title: 'Proposal', description: 'Concierge drafts the proposal from the pricing call. Agent · Week 2 · needs your approval.' },
        { id: 's5', title: 'Signature', description: 'Daniel Reyes signs, and the record moves to Renewed. Daniel Reyes · Week 3.' },
      ],
      on_start: [
        'Concierge drafts the kick-off email. Nothing is sent until you approve it.',
        'Creates two tasks for Priya Nair: the pricing call and the Legal hand-off.',
        `Shows the workflow on ${name}'s record until it finishes.`,
      ],
    },
  },
  {
    id: 'nba-references',
    title: `Answer ${name}'s request for customer references from the call`,
    context: 'Buying intent.',
    description: `${name} asked about customer references on the Sep 2 call, and the evaluation is funded this cycle.`,
    tags: [
      { key: 'buying_intent', label: 'Buying intent' },
      { key: 'draft_ready', label: 'Draft ready' },
      { key: 'email', label: 'Email' },
    ],
    primary_action: { type: 'review_draft', draft_id: 'd-references' },
    details: {
      source: { label: 'The Sep 2 call' },
      ways: [
        {
          id: 'w-references-email',
          title: `Email ${name} about the references`,
          description: 'One email is enough to start.',
          recommended: true,
          action: { type: 'review_draft', draft_id: 'd-references' },
        },
        handToConcierge(
          'w-references-concierge',
          'Concierge follows up until both references are sent and checks with you before each message.',
        ),
      ],
      steps: [],
    },
  },
  {
    id: 'nba-daniel',
    title: 'Bring Daniel Reyes into the renewal',
    context: 'Renewal · closes in 20 days · needs your confirmation.',
    description: 'Daniel Reyes signs above $250K, and the renewal is $480K.',
    tags: [
      { key: 'draft_ready', label: 'Draft ready' },
      { key: 'email', label: 'Email' },
    ],
    primary_action: { type: 'review_draft', draft_id: 'd-daniel' },
    details: {
      source: { label: 'Signing authority on the account' },
      ways: [
        {
          id: 'w-daniel-email',
          title: 'Introduce yourself to Daniel Reyes',
          description: `Copies ${name}, who asked to keep Daniel informed.`,
          recommended: true,
          action: { type: 'review_draft', draft_id: 'd-daniel' },
        },
      ],
      steps: [],
    },
  },
];

const NEW_ITEM = {
  id: 'nba-residency',
  title: 'Confirm the data-residency region before the call',
  context: 'New evidence.',
  description: 'The governance addendum names a region the migration plan does not.',
  is_new: true,
  tags: [{ key: 'email', label: 'Email' }],
  primary_action: { type: 'view_details' },
  details: { source: { label: 'Governance addendum, Aug 28' }, ways: [], steps: [] },
};

export const listMockNextBestActions = ({ entityType, entityId }) => {
  const scenario = readScenario();
  if (scenario === 'fail') return { error: { status: 500, description: 'Mock suggestions failure (?mockNba=fail).' } };

  const checkedAt = new Date(Date.now() - 12 * MINUTE_MS).toISOString();
  const empty = scenario === 'noNba' || scenario === 'empty';
  const removed = started.get(recordKey(entityType, entityId))?.removedIds ?? [];

  let items = empty ? [] : buildItems(nameOf(entityType, entityId)).filter((item) => !removed.includes(item.id));
  if (scenario === 'reviewer' && items[0]) items = [{ ...items[0], reviewer: { name: 'Priya Nair' } }, ...items.slice(1)];
  if (scenario === 'newAction') items = [...items, NEW_ITEM];

  return {
    body: {
      items: items.map((item) => ({ is_new: false, reviewer: null, workflow: null, summary: null, ...item })),
      updated_at: checkedAt,
      checked_at: checkedAt,
      restricted: scenario === 'restricted',
    },
    meta: {},
  };
};

export const startMockNextBestActionWorkflow = ({ entityType, entityId, itemId }) => {
  if (readScenario() === 'start_fail') {
    return { error: { status: 500, description: 'Mock workflow failure (?mockNba=start_fail).' } };
  }
  const name = nameOf(entityType, entityId);
  const item = buildItems(name).find((candidate) => candidate.id === itemId);
  if (!item?.workflow) return { error: { status: 404, description: `No workflow behind ${itemId}` } };

  const key = recordKey(entityType, entityId);
  const removedIds = [...(started.get(key)?.removedIds ?? []), itemId];
  const total = item.workflow.steps.length;
  const workflow = { name: item.workflow.name, step: 1, total };
  started.set(key, { removedIds, workflow });

  return {
    body: {
      workflow: { ...workflow, message: `Step 1 of ${total}: the kick-off email is drafted for your approval.` },
    },
    meta: {},
  };
};

/** The workflow running on a record, for the entity header (`active_workflow`). */
export const getMockActiveWorkflow = (entityType, entityId) => started.get(recordKey(entityType, entityId))?.workflow ?? null;

export const __setMockNbaScenario = (value) => {
  scenarioOverride = value;
};

export const __resetMockNextBestActionsForTests = () => {
  scenarioOverride = null;
  started = new Map();
};
